/**
 * evaluator.ts —— 求值器
 *
 * 把 AST 跑起来。核心只有一件事：
 *
 *   对每条语句，从 registry 取出根对象 → 依次执行链上每个方法 →
 *   把上一个方法的返回值传给下一个方法作为 this 值。
 *
 * 三条不可破的纪律：
 *
 * ① **绝不 eval**。这里没有 `eval`、没有 `new Function`、
 *    没有 `obj[name]` 这种动态属性访问。方法是**查表**得到函数引用后调用的，
 *    表里有什么就只能调什么。AI 编造一个不存在的方法名，查不到就是 NameError。
 *
 * ② **执行结果必须如实**。每个方法返回的 `text` 或 method 的 `successText`
 *    是唯一可以展示给用户的成功文案。求值器不生成、不猜测、不美化。
 *
 * ③ **失败不中断整批**。一条语句失败后，后续语句继续执行并各自记录结果 ——
 *    因为多条指令往往互相独立（「切主题 + 标记已读」），
 *    一条失败不该拖累另一条。UI 会把每条的结果都列出来。
 */

import { registry, RuntimeError, type CallContext, type MethodDef } from './registry';
import { trace } from '@/ai/trace';
import type { ChainNode, ProgramNode } from './ast';
import type { EvalError, EvalResult } from '@/types/il';
import type { ParamSpec } from '@/types/il';

/** 二次确认回调：返回 true 表示用户同意执行 */
export type ConfirmHandler = (text: string) => Promise<boolean>;

export interface EvalOptions {
  /** 某个方法声明了 requireConfirm 时会调用它。不给则直接视为拒绝 */
  onConfirm?: ConfirmHandler;
  /** 单条语句的超时（毫秒）。防御写坏的 API 把界面卡死 */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * 执行整个程序。
 *
 * 返回一个与 statements 一一对应的结果数组，顺序不变 ——
 * 这样 UI 可以按用户/模型的原始顺序逐条展示。
 */
export async function evaluate(
  program: ProgramNode,
  options: EvalOptions = {},
): Promise<EvalResult[]> {
  const results: EvalResult[] = [];
  for (const stmt of program.statements) {
    results.push(await evalStatement(stmt, options));
  }
  return results;
}

/** 执行单条语句，永远返回结果（不抛异常） */
async function evalStatement(stmt: ChainNode, options: EvalOptions): Promise<EvalResult> {
  try {
    const run = await runChain(stmt, options);
    return {
      command: stmt.source,
      ok: true,
      desc: run.text,
      value: run.value,
      isQuery: run.isQuery,
      descIsReceipt: run.descIsReceipt,
    };
  } catch (err) {
    return { command: stmt.source, ok: false, desc: '', error: toEvalError(err) };
  }
}

/** 一条链跑完的产物：给用户看的文案 + 给 AI 看的原始值 */
interface ChainRun {
  text: string;
  value: unknown;
  /** 这条链里是否出现过 [查] 方法 */
  isQuery: boolean;
  /** text 是否只是查类方法的机械回执（UI 可折叠） */
  descIsReceipt: boolean;
}

/**
 * 跑一条链，返回这一条的最终成功文案。
 *
 * 文案的取用顺序：
 *   ① 方法显式返回的 `text`（最精确，能引用返回值）
 *   ② 方法的 `successText` 声明（次精确，能引用实参）
 *   两者都没有 → 用一个诚实的兜底文案「已执行 xxx」，但**不**编造效果描述。
 */
async function runChain(stmt: ChainNode, options: EvalOptions): Promise<ChainRun> {
  const rootName = stmt.root;

  if (!registry.hasObject(rootName)) {
    throw new RuntimeError(
      `不认识「${rootName}」这个东西。可用的有：${registry
        .listObjects()
        .map((o) => o.name)
        .join('、')}`,
      'NameError',
    );
  }

  let value: unknown = undefined;
  /** 逐环累积的文案。只保留最后一环的（最终状态才是有意义的） */
  let lastText = '';
  /** 这条链里是否出现过 [查] 方法 */
  let isQuery = false;
  /** 文案是不是「查类方法的机械回执」（见 EvalResult.descIsReceipt） */
  let descIsReceipt = false;

  for (const call of stmt.calls) {
    // 空方法名 = 解析器的语法糖（`explain('文字')`）。
    // 换成注册层给出的默认方法名。只有第一个 call 可能是空的，
    // 因为糖只允许出现在根对象后面。
    const methodName = call.name || defaultMethodFor(rootName);

    const method = registry.lookup(rootName, methodName);
    if (!method) {
      throw new RuntimeError(
        `「${rootName}」没有「${call.name || methodName}」这个方法。可用方法：${registry
          .lookupAll(rootName)
          .map((m) => m.name)
          .join('、')}`,
        'NameError',
      );
    }

    // 参数校验（类型、必填、范围、枚举）
    const args = validateArgs(method, call.args);

    // 记下这条链里有没有查询方法 —— 决定上层要不要走「回答轮」
    if (method.kind === 'query') isQuery = true;

    // 二次确认
    if (method.requireConfirm) {
      const confirmText = method.requireConfirm(args);
      if (confirmText) {
        // 确认是一次「链路暂停」，值得单独留痕 —— 用户点取消后
        // 结果里的错误是「用户取消了」，不看这行很难理解这条是怎么失败的
        trace.note(`⏸ 等待二次确认：${confirmText}`);
        const agreed = options.onConfirm ? await options.onConfirm(confirmText) : false;
        trace.note(agreed ? '▶ 用户已同意' : '⏹ 用户已取消');
        if (!agreed) {
          throw new RuntimeError('用户取消了这项操作', 'RuntimeError');
        }
      }
    }

    const ctx: CallContext = { value, registry };

    const outcome = await withTimeout(
      Promise.resolve(method.run(args, ctx)),
      options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      `${rootName}.${method.name}`,
    );

    if (outcome && typeof outcome === 'object') {
      value = outcome.value;
      // 显式文案优先；没有就用声明的 successText；再没有就兜底
      const explicit = outcome.text ?? resolveSuccessText(method, args);
      lastText = explicit ?? `已执行 ${rootName}.${call.name}`;
      // 只有「回退到 successText/兜底」而没有显式 text 时，才算机械回执。
      // 若方法主动给了 text（如 contact.create 的「已经存在」），那是真信息，要展示。
      descIsReceipt = outcome.text === undefined && method.kind === 'query';
    } else {
      value = undefined;
      lastText = resolveSuccessText(method, args) ?? `已执行 ${rootName}.${call.name}`;
      descIsReceipt = method.kind === 'query';
    }
  }

  return { text: lastText, value, isQuery, descIsReceipt };
}

/** successText 支持函数形式（需要引用实参时） */
function resolveSuccessText(method: MethodDef, args: unknown[]): string | undefined {
  if (typeof method.successText === 'function') return method.successText(args);
  return method.successText || undefined;
}

/** 取某根对象的默认方法名，用于解析语法糖留下的空方法名 */
function defaultMethodFor(root: string): string {
  return registry.getDefaultMethod(root);
}

/**
 * 参数校验。
 *
 * 这一步的意义不只是防错 —— 它让**错误信息变得具体**：
 * 「第 1 个参数应该是 'light'/'dark'/'auto' 之一，你给的是 'rainbow'」
 * 比「参数不合法」有用得多。因为这条错误要回喂给 AI 做二轮（D10），
 * 说得越准，模型越容易改对。
 */
function validateArgs(method: MethodDef, args: unknown[]): unknown[] {
  const specs = method.params ?? [];

  // 必填数量校验
  const requiredCount = specs.filter((s) => s.required !== false).length;
  if (args.length < requiredCount) {
    throw new RuntimeError(
      `${method.name} 至少需要 ${requiredCount} 个参数，只给了 ${args.length} 个`,
      'TypeError',
    );
  }
  if (args.length > specs.length) {
    throw new RuntimeError(
      `${method.name} 最多接受 ${specs.length} 个参数，却给了 ${args.length} 个`,
      'TypeError',
    );
  }

  return args.map((val, i) => {
    const spec = specs[i];
    if (!spec) return val; // 没有规格的额外参数原样放行

    const where = `第 ${i + 1} 个参数（${spec.name}）`;

    switch (spec.type) {
      case 'string':
        if (typeof val !== 'string') throw typeErr(`${where}应该是文字`);
        return val;

      case 'number': {
        if (typeof val !== 'number') throw typeErr(`${where}应该是数字`);
        if (spec.min !== undefined && val < spec.min) {
          throw typeErr(`${where}不能小于 ${spec.min}`);
        }
        if (spec.max !== undefined && val > spec.max) {
          throw typeErr(`${where}不能大于 ${spec.max}`);
        }
        return val;
      }

      case 'boolean':
        if (typeof val !== 'boolean') throw typeErr(`${where}应该是 true 或 false`);
        return val;

      case 'enum': {
        const allowed = spec.values ?? [];
        if (typeof val !== 'string' || !allowed.includes(val)) {
          throw typeErr(
            `${where}只能是 ${allowed.map((v) => `'${v}'`).join(' / ')} 之一，` +
              `收到的是 ${JSON.stringify(val)}`,
          );
        }
        return val;
      }

      case 'handle':
        // 句柄由链的前一环产出，类型由产出方保证；这里只做存在性检查
        if (val === undefined || val === null) {
          throw typeErr(`${where}缺失。这个参数要用前面某一步的返回值`);
        }
        return val;

      default:
        return val;
    }
  });
}

function typeErr(msg: string): RuntimeError {
  return new RuntimeError(msg, 'TypeError');
}

/** 给方法执行加超时，防止一个卡住的 API 冻结整个界面 */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new RuntimeError(`${label} 执行超过 ${Math.round(ms / 1000)} 秒，已中止`));
    }, ms);

    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/** 把任意异常转成 EvalError */
function toEvalError(err: unknown): EvalError {
  if (err instanceof RuntimeError) return err.toEvalError();
  return {
    kind: 'RuntimeError',
    message: err instanceof Error ? err.message : String(err),
  };
}

/** 供其它模块复用的类型导出 */
export type { ParamSpec };
